import type { Period, PoolSort, VolumePeriod, YieldPeriod } from "../types";

export interface CatalogFilters {
  minTvl: string;
  minVolume: string;
  hasRewards: boolean;
  noWarnings: boolean;
  hasAdaptiveFee: boolean;
  isWavebreak: boolean;
  hasLockedLiquidity: boolean;
  minLockedLiquidityPercent: string;
  withoutMemes: boolean;
}

export type CatalogColumn =
  | "priceTrend"
  | "tokenA"
  | "tokenB"
  | "yieldTvl"
  | "volume"
  | "tvl"
  | "fees"
  | "effectiveLps"
  | "rewards"
  | "price"
  | "address";

export const defaultCatalogFilters: CatalogFilters = {
  minTvl: "",
  minVolume: "",
  hasRewards: false,
  noWarnings: false,
  hasAdaptiveFee: false,
  isWavebreak: false,
  hasLockedLiquidity: false,
  minLockedLiquidityPercent: "1",
  withoutMemes: false,
};

export const defaultCatalogColumns: Record<CatalogColumn, boolean> = {
  priceTrend: false,
  tokenA: false,
  tokenB: false,
  yieldTvl: true,
  volume: true,
  tvl: true,
  fees: true,
  effectiveLps: true,
  rewards: true,
  price: false,
  address: false,
};

export const columnOptions: { value: CatalogColumn; label: string; sort?: PoolSort }[] = [
  { value: "priceTrend", label: "Trend 7D" },
  { value: "tokenA", label: "Token A mint" },
  { value: "tokenB", label: "Token B mint" },
  { value: "yieldTvl", label: "Yield / TVL", sort: "yieldTvl" },
  { value: "volume", label: "Volume", sort: "volume" },
  { value: "tvl", label: "TVL", sort: "tvl" },
  { value: "fees", label: "Fees", sort: "fees" },
  { value: "effectiveLps", label: "Effective LPs" },
  { value: "rewards", label: "Rewards", sort: "rewards" },
  { value: "price", label: "Current price" },
  { value: "address", label: "Pool address" },
];

export interface CatalogPreferences {
  period: Period;
  volumePeriod: VolumePeriod;
  yieldPeriod: YieldPeriod;
  sort: PoolSort;
  direction: "asc" | "desc";
  enabledColumns: CatalogColumn[];
  appliedSearch: string;
  appliedFilters: CatalogFilters;
}

const catalogPreferencesStorageKey = "auto-pools:catalog-preferences:v1";
const legacyCatalogFiltersStorageKey = "auto-pools:catalog-filters:v1";
const maxStoredPreferencesLength = 8_192;
const periods: Period[] = ["24h", "4h"];
const volumePeriods: VolumePeriod[] = ["5m", "15m", "30m", "1h", "2h", "4h", "8h", "24h", "7d"];
const yieldPeriods: YieldPeriod[] = ["15m", "30m", "1h", "4h", "8h", "24h", "7d", "30d", "365d"];
const poolSorts: PoolSort[] = ["fees", "tvl", "volume", "rewards", "feesTvl", "yieldTvl"];
const directions = ["asc", "desc"] as const;
const catalogColumns = columnOptions.map(({ value }) => value);

export const defaultCatalogPreferences: CatalogPreferences = {
  period: "24h",
  volumePeriod: "24h",
  yieldPeriod: "365d",
  sort: "fees",
  direction: "desc",
  enabledColumns: catalogColumns.filter((column) => defaultCatalogColumns[column]),
  appliedSearch: "",
  appliedFilters: defaultCatalogFilters,
};

function allowedValue<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && allowed.includes(value as T) ? (value as T) : fallback;
}

function storedDecimal(value: unknown, fallback: string, kind: "usd" | "percent"): string {
  if (typeof value !== "string" || value.length > 32) return fallback;
  try {
    return normalizeDecimal(value, kind);
  } catch {
    return fallback;
  }
}

function storedFilters(value: unknown): CatalogFilters | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const boolean = (key: keyof CatalogFilters, fallback: boolean) =>
    typeof record[key] === "boolean" ? record[key] : fallback;
  return {
    minTvl: storedDecimal(record.minTvl, defaultCatalogFilters.minTvl, "usd"),
    minVolume: storedDecimal(record.minVolume, defaultCatalogFilters.minVolume, "usd"),
    hasRewards: boolean("hasRewards", defaultCatalogFilters.hasRewards),
    noWarnings: boolean("noWarnings", defaultCatalogFilters.noWarnings),
    hasAdaptiveFee: boolean("hasAdaptiveFee", defaultCatalogFilters.hasAdaptiveFee),
    isWavebreak: boolean("isWavebreak", defaultCatalogFilters.isWavebreak),
    hasLockedLiquidity: boolean("hasLockedLiquidity", defaultCatalogFilters.hasLockedLiquidity),
    minLockedLiquidityPercent: storedDecimal(
      record.minLockedLiquidityPercent,
      defaultCatalogFilters.minLockedLiquidityPercent,
      "percent",
    ),
    withoutMemes: boolean("withoutMemes", defaultCatalogFilters.withoutMemes),
  };
}

function storedColumns(
  value: unknown,
  addEffectiveLps = false,
  removePriceTrend = false,
): CatalogColumn[] {
  if (
    !Array.isArray(value) ||
    value.length > catalogColumns.length ||
    value.some(
      (column) => typeof column !== "string" || !catalogColumns.includes(column as CatalogColumn),
    ) ||
    new Set(value).size !== value.length
  ) {
    return [...defaultCatalogPreferences.enabledColumns];
  }
  // The trend column became opt-in in v3: an older record's "on" was the old default, not a choice.
  const columns = (value as CatalogColumn[]).filter(
    (column) => !(removePriceTrend && column === "priceTrend"),
  );
  if (addEffectiveLps && !columns.includes("effectiveLps")) {
    const rewards = columns.indexOf("rewards");
    columns.splice(rewards < 0 ? columns.length : rewards, 0, "effectiveLps");
  }
  return columns;
}

function storedPreferences(value: unknown, legacyFilters: CatalogFilters): CatalogPreferences {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {
      ...defaultCatalogPreferences,
      enabledColumns: [...defaultCatalogPreferences.enabledColumns],
      appliedFilters: legacyFilters,
    };
  }
  const record = value as Record<string, unknown>;
  if (record.version !== 1 && record.version !== 2 && record.version !== 3) {
    return {
      ...defaultCatalogPreferences,
      enabledColumns: [...defaultCatalogPreferences.enabledColumns],
      appliedFilters: legacyFilters,
    };
  }
  return {
    period: allowedValue(record.period, periods, defaultCatalogPreferences.period),
    volumePeriod: allowedValue(
      record.volumePeriod,
      volumePeriods,
      defaultCatalogPreferences.volumePeriod,
    ),
    yieldPeriod: allowedValue(
      record.yieldPeriod,
      yieldPeriods,
      defaultCatalogPreferences.yieldPeriod,
    ),
    sort: allowedValue(record.sort, poolSorts, defaultCatalogPreferences.sort),
    direction: allowedValue(record.direction, directions, defaultCatalogPreferences.direction),
    enabledColumns: storedColumns(
      record.enabledColumns,
      record.version === 1,
      record.version !== 3,
    ),
    appliedSearch:
      typeof record.appliedSearch === "string" && record.appliedSearch.length <= 100
        ? record.appliedSearch.trim()
        : defaultCatalogPreferences.appliedSearch,
    appliedFilters: storedFilters(record.appliedFilters) ?? legacyFilters,
  };
}

export function readCatalogPreferences(): CatalogPreferences {
  const defaults = storedPreferences(null, { ...defaultCatalogFilters });
  try {
    const legacy = localStorage.getItem(legacyCatalogFiltersStorageKey);
    let legacyFilters = defaults.appliedFilters;
    if (legacy && legacy.length <= 2_048) {
      try {
        legacyFilters = storedFilters(JSON.parse(legacy)) ?? legacyFilters;
      } catch {
        // A malformed legacy value must not hide a valid current preference object.
      }
    }
    const stored = localStorage.getItem(catalogPreferencesStorageKey);
    if (!stored || stored.length > maxStoredPreferencesLength) {
      return storedPreferences(null, legacyFilters);
    }
    try {
      return storedPreferences(JSON.parse(stored), legacyFilters);
    } catch {
      return storedPreferences(null, legacyFilters);
    }
  } catch {
    return defaults;
  }
}

export function writeCatalogPreferences(preferences: CatalogPreferences): void {
  try {
    const normalized = storedPreferences(
      { version: 3, ...preferences },
      {
        ...defaultCatalogFilters,
      },
    );
    localStorage.setItem(
      catalogPreferencesStorageKey,
      JSON.stringify({ version: 3, ...normalized }),
    );
  } catch {
    // Catalog preferences are optional when browser storage is unavailable.
  }
}

function normalizeDecimal(value: string, kind: "usd" | "percent"): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const pattern = kind === "usd" ? /^\d{1,24}(?:\.\d{1,2})?$/ : /^\d{1,3}(?:\.\d{1,6})?$/;
  if (!pattern.test(trimmed))
    throw new Error(
      kind === "usd"
        ? "Enter a USD amount with up to 2 decimal places."
        : "Locked liquidity must be greater than 0% and at most 100%.",
    );
  const [whole = "0", fraction = ""] = trimmed.split(".");
  const normalizedWhole = whole.replace(/^0+(?=\d)/, "");
  const normalizedFraction = fraction.replace(/0+$/, "");
  const normalized = normalizedFraction
    ? `${normalizedWhole}.${normalizedFraction}`
    : normalizedWhole;
  if (
    kind === "percent" &&
    (normalized === "0" ||
      BigInt(normalizedWhole) > 100n ||
      (normalizedWhole === "100" && Boolean(normalizedFraction)))
  )
    throw new Error("Locked liquidity must be greater than 0% and at most 100%.");
  return normalized === "0" && kind === "usd" ? "" : normalized;
}

export function normalizeCatalogFilters(filters: CatalogFilters): CatalogFilters {
  return {
    ...filters,
    minTvl: normalizeDecimal(filters.minTvl, "usd"),
    minVolume: normalizeDecimal(filters.minVolume, "usd"),
    minLockedLiquidityPercent: filters.hasLockedLiquidity
      ? normalizeDecimal(filters.minLockedLiquidityPercent || "1", "percent")
      : "1",
  };
}

export function activeFilterCount(filters: CatalogFilters): number {
  return (
    Number(Boolean(filters.minTvl)) +
    Number(Boolean(filters.minVolume)) +
    Number(filters.hasRewards) +
    Number(filters.noWarnings) +
    Number(filters.hasAdaptiveFee) +
    Number(filters.isWavebreak) +
    Number(filters.hasLockedLiquidity) +
    Number(filters.withoutMemes)
  );
}
